import { Avatar } from '@octopus-do/octopus-storybook'
import {
  InboxActions,
  type InboxItem as Item,
  itemLoader,
  itemPath,
} from '@store/reducers/inbox'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { selectIsLoading } from '@/store'
import { clsJoin, isPlainLeftClick } from '@/utils'
import css from './styles.module.css'
import { describeItem, relativeTime } from './utils'

interface Props {
  item: Item
  now: number
}

// The row's DTO carries no palette colour for the person; the first
// collaborator colour is the neutral one an invite gets, and it gives the
// initials a filled disc with light text, as in the members list
const AVATAR_COLOR = 'collab-10'

// One row of the activity list: where it happened on the first line, who did
// what and how long ago on the second, a dot on the right while unread
export const InboxItem: React.FC<Props> = ({ item, now }) => {
  const dispatch = useDispatch()
  const line = describeItem(item)
  const unread = item.readAt === null
  const loading = useSelector(selectIsLoading(itemLoader(item)))

  // A plain click loads the project first and then goes there; Cmd-click and
  // the middle button stay with the link, the row only counts as read
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      if (unread) {
        dispatch(
          InboxActions.markRead({ workspace: item.workspace, ids: [item.id] })
        )
      }

      return
    }

    event.preventDefault()

    if (loading) {
      return
    }

    dispatch(InboxActions.open({ item }))
  }

  return (
    <Link
      to={itemPath(item)}
      className={clsJoin(
        css.item,
        unread && css.unread,
        loading && css.loading
      )}
      data-testid="inbox_item"
      data-unread={unread}
      aria-busy={loading}
      onClick={handleClick}
    >
      <span className={css.avatar}>
        <Avatar
          name={item.actor?.name || line.avatar}
          media={item.actor?.avatar ?? null}
          color={AVATAR_COLOR}
          size="sm"
        />
      </span>
      <span className={css.body}>
        <span className={css.title}>
          {line.title}
          {line.project && (
            <span className={css.project}> · {line.project}</span>
          )}
        </span>
        <span className={css.meta}>
          {line.actor} {line.action}
          <span className={css.sep}>•</span>
          {relativeTime(item.createdAt, now)}
        </span>
      </span>
      <span className={css.dot} />
    </Link>
  )
}
